import { useSuspenseQuery } from '@apollo/client';
import { Suspense, useMemo } from 'react';

import { Carousel } from '@xing-com/carousel';
import {
  ModuleHeader,
  UpsellBanner,
  WarningMessageBox,
} from '@xing-com/crate-entity-pages-common';
import {
  CompanyRecommendationCard,
  RecommendedCompaniesDocument,
} from '@xing-com/crate-entity-pages-company-recommendations';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';

import { CompanyRecommendationsFallback } from './company-recommendations-module.skeleton';
import * as Styled from './company-recommendations-module.styles';

const CompanyRecommendationsModuleInner: React.FC = () => {
  const { pageContext } = usePageContext();

  const { data, refetch } = useSuspenseQuery(RecommendedCompaniesDocument, {
    variables: {
      id: pageContext.companyId ?? '',
    },
    errorPolicy: 'all',
  });

  const cards = useMemo(() => {
    const recommendedCompanies = data?.company?.relatedCompanies?.collection;

    return recommendedCompanies
      ?.map((node) => node?.company)
      .filter((company) => !!company);
  }, [data]);

  if (!cards) {
    return (
      <div data-testid="errorContainer">
        <ModuleHeader headlineCopyKey="CP_RECOMMENDS_ADVERTISEMENT_ABOUT_US" />
        <WarningMessageBox
          headerText="EP_ERROR_HEADER"
          bodyText="EP_ERROR_BODY"
          buttonText="EP_ERROR_RELOAD_CTA"
          onClick={() => refetch()}
        />
      </div>
    );
  }

  const upsellActive = pageContext.upsellActive;

  if (cards.length === 0) return null;

  return (
    <div data-testid={'COMPANY_RECOMMENDATIONS_MODULE'}>
      <ModuleHeader headlineCopyKey="CP_RECOMMENDS_ADVERTISEMENT_ABOUT_US" />
      {upsellActive ? (
        <Styled.UpsellLayout>
          <UpsellBanner
            headerCopyKey="EP_UPSELL_COMPANY_RECOMMENDED_HEADER"
            bodyCopyKey="EP_UPSELL_COMPANY_RECOMMENDED_BODY"
            moduleName="companyrecommendationsmodule"
          />
        </Styled.UpsellLayout>
      ) : null}
      <Carousel
        itemWidthConfined="50%"
        itemWidth="80%"
        itemWidthWide="280px"
        itemSpacingConfined="16px"
      >
        {cards.map(({ id, companyName, logos, links, entityPage }) => (
          <CompanyRecommendationCard
            key={id}
            profileImage={logos?.logo128px ?? undefined}
            profileName={companyName ?? ''}
            link={links?.public ?? undefined}
            backgroundImageUrl={entityPage?.coverImage?.[0]?.url ?? undefined}
          />
        ))}
      </Carousel>
    </div>
  );
};

export const CompanyRecommendationsModule: React.FC = () => {
  return (
    <Suspense fallback={<CompanyRecommendationsFallback />}>
      <CompanyRecommendationsModuleInner />
    </Suspense>
  );
};
